import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { GetBillTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillTosDataView } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import SelectShipToModal from "@WhiteCap/Components/SelectShipToModal";
import useDebounce from "@WhiteCap/CustomHooks/useDebounce";
import CustomCross from "@WhiteCap/Icons/CustomCross";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks, useSelector } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    modalIsOpen: boolean;
    extendedStyles?: SelectCustomerModalStyles;
    billToParameter: GetBillTosApiParameter;
    onModalClose: () => void;
    billTo?: BillToModel;
    onSelect: (billTo: BillToModel) => void;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const billTosDataView = getBillTosDataView(state, props.billToParameter);
    const key = getDataViewKey(props.billToParameter);
    const rawDataView = state.data.billTos.dataViews[key];
    const pagination = rawDataView?.pagination;
    return {
        billTosDataView,
        pagination,
        session: state.context.session,
        billTosById: state.data.billTos.byId,
    };
};

const mapDispatchToProps = {
    loadBillTos,
};

type Props = OwnProps &
    PaginationPresentationProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface SelectCustomerModalStyles {
    searchCustomerText?: TextFieldPresentationProps;
    searchButton?: ButtonPresentationProps;
    container?: GridContainerProps;
    accountGridItem?: GridItemProps;
    searchGridItem?: GridItemProps;
    selectButton?: ButtonPresentationProps;
    modal?: ModalPresentationProps;
    customerNameText?: TypographyPresentationProps;
    selectButtonGridItem?: GridItemProps;
    customerNumberGridItem?: GridItemProps;
    customerNumberText?: TypographyPresentationProps;
    pagination?: PaginationPresentationProps;
}

export const selectCustomerModalStyles: SelectCustomerModalStyles = {
    container: {
        css: css`
            margin-bottom: 8px;
            padding: 8px;
            background-color: #f7f8f9;
            > div[class^="GridOffset"] {
                margin: 0px;
                max-width: 100%;
            }
        `,
    },
    accountGridItem: {
        width: 9,
    },
    customerNumberGridItem: {
        width: 12,
        css: css`
            padding: 0px;
        `,
    },
    searchGridItem: {
        width: 12,
        css: css`
            height: 40px;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 40px !important;
        `,
    },
    selectButton: {
        variant: "tertiary",
        typographyProps: {
            css: css`
                font-size: 14px;
                text-align: center;
                font-weight: 450;
                line-height: 24px;
                text-transform: uppercase;
            `,
        },
        css: css`
            padding: 4px 8px;
            justify-content: center;
            align-items: center;
            min-width: max-content;
        `,
    },
    modal: {
        cssOverrides: {
            modalContent: css`
                padding: 16px 24px !important;
                > div[class^="GridOffset"] {
                    margin: 0px;
                }
            `,
            modalTitle: css`
                margin-bottom: 35px;
            `,
        },
    },
    selectButtonGridItem: {
        width: 3,
        css: css`
            padding: 0px;
            justify-content: end;
        `,
    },
    customerNumberText: {
        css: css`
            font-size: 12px;
            font-weight: 450;
        `,
    },
    customerNameText: {
        css: css`
            font-size: 12px;
            text-transform: uppercase;
        `,
    },
};

const SelectCustomerModal = ({
    extendedStyles,
    modalIsOpen,
    onModalClose,
    billTo,
    billToParameter,
    loadBillTos,
    onSelect,
    session,
    billTosById,
}: Props) => {
    const [styles] = useState(() => mergeToNew(selectCustomerModalStyles, extendedStyles));
    const [modalKey, setModalKey] = React.useState(0);
    const [searchValue, setSearchValue] = React.useState("");
    const [isShipToModalOpen, setIsShipToModalOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<BillToModel | undefined>(undefined);
    const [customerParameter, setCustomerParameter] = useState<GetBillTosApiParameter>({
        page: 1,
        pageSize: 8,
        additionalExpands: ["accounts"],
    });

    const { customBillTosDataView, customPagination } = useSelector((state: ApplicationState) => {
        const customBillTosDataView = getBillTosDataView(state, customerParameter);
        const key = getDataViewKey(customerParameter);
        const rawDataView = state.data.billTos.dataViews[key];
        const customPagination = rawDataView?.pagination;
        return {
            customBillTosDataView,
            customPagination,
        };
    });

    const debouncedSearchValue = useDebounce(searchValue, 500);
    const lastSearchRef = useRef<string>("");
    const billToId = session.billToId ?? "";
    const selectedBillTo = billTosById[billToId];

    useEffect(() => {
        if (!modalIsOpen) {
            return;
        }

        const dataView = customBillTosDataView;

        if (!dataView.value && !dataView.isLoading) {
            loadBillTos(customerParameter);
        }
    }, [customerParameter, modalIsOpen]);

    useEffect(() => {
        if (!modalIsOpen) {
            return;
        }
        const current = debouncedSearchValue.trim();
        const shouldSearch = (current.length >= 3 || current.length === 0) && current !== lastSearchRef.current;
        if (shouldSearch) {
            lastSearchRef.current = current;
            if (lastSearchRef.current !== customerParameter.filter) {
                setCustomerParameter({
                    ...customerParameter,
                    filter: current,
                    page: 1,
                });
            }
        }
    }, [debouncedSearchValue, modalIsOpen]);

    const handleSearchAddressesChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        const value = event.target.value ?? "";
        setSearchValue(value);
    };

    const handleSetSearchFilter = () => {
        setSearchValue("");
        lastSearchRef.current = "";
        setCustomerParameter({
            ...customerParameter,
            filter: "",
            page: 1,
        });
    };

    const modalCloseHandler = () => {
        setSearchValue("");
        lastSearchRef.current = "";
        setCustomerParameter({
            ...customerParameter,
            filter: "",
            page: 1,
        });
        onModalClose();
    };

    const selectCustomerHandler = (billToId?: string) => {
        if (customBillTosDataView.value && billToId) {
            let selectedBillTo: BillToModel | undefined = customBillTosDataView.value.find(
                billTo => billTo.id === billToId,
            );
            if (!selectedBillTo && billTo?.id === billToId) {
                selectedBillTo = billTo;
            }
            if (selectedBillTo) {
                setSelectedCustomer(selectedBillTo);
                setModalKey(prev => prev + 1);
                setIsShipToModalOpen(true);
            }
        }
    };

    const onChangePage = (page: number) => {
        setCustomerParameter({
            ...customerParameter,
            page,
        });
    };

    const onChangeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        let newPageSize = Number(event.target.value);
        setCustomerParameter({
            ...customerParameter,
            page: 1,
            pageSize: newPageSize,
        });
    };

    function getSortedBillTos(
        billTos: BillToModel[] | undefined,
        selectedBillTo: BillToModel | undefined,
        currentPage: number,
        filterValue?: string,
    ): BillToModel[] {
        if (!billTos) {
            return [];
        }

        if (!selectedBillTo) {
            return billTos;
        }

        const filter = filterValue?.trim().toLowerCase() ?? "";
    const hasSearch = filter.length > 0;

    // For searches: only include selectedBillTo if it matches search text
    if (hasSearch) {
        const matchesSelected =
            selectedBillTo.customerNumber?.toLowerCase().includes(filter) ||
            selectedBillTo.properties?.customerName?.toLowerCase().includes(filter);

        const filteredBillTos = billTos.filter(
            b => b.customerNumber !== selectedBillTo.customerNumber
        );

        return matchesSelected ? [selectedBillTo, ...filteredBillTos] : filteredBillTos;
    }

        if (currentPage > 1) {
            return billTos.filter(b => b.customerNumber !== selectedBillTo.customerNumber);
        }

        const filtered = billTos.filter(b => b.customerNumber !== selectedBillTo.customerNumber);
        return [selectedBillTo, ...filtered];
    }

    const currentTotalCount = customPagination?.totalItemCount && customPagination?.totalItemCount + 1;

    return (
        <>
            {!isShipToModalOpen && (
                <Modal
                    isOpen={modalIsOpen}
                    key={modalKey}
                    headline={translate("Accounts")}
                    handleClose={modalCloseHandler}
                    {...styles.modal}
                >
                    <GridContainer>
                        <GridItem {...styles.searchGridItem}>
                            <TextField
                                {...styles.searchCustomerText}
                                label={`${translate("Select Account to shop with")} (${currentTotalCount})`}
                                value={searchValue}
                                onChange={handleSearchAddressesChanged}
                                onSelect={() => selectCustomerHandler}
                                iconProps={searchValue ? { src: CustomCross } : { src: "Search" }}
                                iconClickableProps={{
                                    onClick: () => {
                                        handleSetSearchFilter();
                                    },
                                    type: "button",
                                }}
                                placeholder={translate("Search by Account # or Name")}
                            />
                        </GridItem>
                    </GridContainer>
                    {(() => {
                        const sortedBillTos = getSortedBillTos(
                            customBillTosDataView?.value,
                            selectedBillTo,
                            customPagination?.currentPage ?? 1,
                            customerParameter.filter,
                        );

                        if (customBillTosDataView?.isLoading) {
                            return <Typography>{translate("Loading...")}</Typography>;
                        }

                        if (sortedBillTos.length === 0) {
                            return <Typography>{translate("No matching accounts found.")}</Typography>;
                        }

                        return sortedBillTos.map(billTo => (
                            <GridContainer key={billTo.id} {...styles.container}>
                                <GridItem {...styles.accountGridItem}>
                                    <GridContainer>
                                        <GridItem {...styles.customerNumberGridItem}>
                                            <Typography {...styles.customerNumberText}>
                                                #{translate(billTo.customerNumber)}
                                            </Typography>
                                        </GridItem>
                                        <GridItem {...styles.customerNumberGridItem}>
                                            <Typography {...styles.customerNameText}>
                                                {billTo.properties?.customerName}
                                            </Typography>
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>

                                {selectedBillTo?.customerNumber !== billTo.customerNumber && (
                                    <GridItem {...styles.selectButtonGridItem}>
                                        <Button
                                            {...styles.selectButton}
                                            type="button"
                                            aria-label="Select Account"
                                            onClick={() => selectCustomerHandler(billTo.id)}
                                            data-test-selector="selectCustomerModal_accountSelect"
                                        >
                                            {translate("Select")}
                                        </Button>
                                    </GridItem>
                                )}
                            </GridContainer>
                        ));
                    })()}
                    {customPagination &&
                        customPagination?.totalItemCount > (customPagination?.defaultPageSize ?? 8) && (
                            <Pagination
                                {...styles.pagination}
                                currentPage={customPagination?.currentPage}
                                resultsPerPage={customPagination!.pageSize ?? 8}
                                resultsCount={customPagination!.totalItemCount}
                                resultsPerPageOptions={customPagination!.pageSizeOptions}
                                onChangePage={onChangePage}
                                onChangeResultsPerPage={onChangeResultsPerPage}
                                prevPage={customPagination!.prevPageUri}
                                nextPage={customPagination!.nextPageUri}
                            />
                        )}
                </Modal>
            )}
            {isShipToModalOpen && (
                <SelectShipToModal
                    modalIsOpen={isShipToModalOpen}
                    key={modalKey}
                    onModalClose={() => {
                        setSearchValue("");
                        setIsShipToModalOpen(false);
                        setSelectedCustomer(undefined);
                    }}
                    billTo={selectedCustomer}
                    billToParameter={{
                        ...billToParameter,
                        customerNumber: selectedCustomer?.customerNumber ?? "",
                    }}
                    onSelect={onSelect}
                    showBackButton={true}
                />
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectCustomerModal);
